import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import ApiHandler from "../../../Handler/ApiHandler";
import { formatDuration } from "../../../Common/DurationCommon";
import "./catalogue-detail.css";

const formatPrice = (price) => {
  if (price === null || price === undefined) {
    return "-";
  }

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(price);
};

function CatalogueDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [prestation, setPrestation] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const resolvePhotoUrl = React.useCallback((path) => {
    if (!path) {
      return "";
    }

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    if (path.startsWith("/api/")) {
      return path;
    }

    return `/api${path}`;
  }, []);

  React.useEffect(() => {
    ApiHandler.get(
      { route: "public_catalogue_prestation_get", params: { id } },
      (response) => {
        if (response.status >= 400) {
          setError("Prestation introuvable.");
          setLoading(false);
          return;
        }

        setPrestation(response.data || null);
        setLoading(false);
      }
    );
  }, [id]);

  if (loading) {
    return <p className="catalogue-detail-state">Chargement...</p>;
  }

  if (error || !prestation) {
    return (
      <div className="catalogue-detail-wrap">
        <p className="catalogue-detail-state">{error || "Prestation introuvable."}</p>
        <button
          type="button"
          className="catalogue-detail-back"
          onClick={() => navigate("/public/catalogue")}
        >
          Retour au catalogue
        </button>
      </div>
    );
  }

  return (
    <section className="catalogue-detail-wrap">
      <article className="catalogue-detail-card">
        <div className="catalogue-detail-head">
          <button
            type="button"
            className="catalogue-detail-back"
            onClick={() => navigate("/public/catalogue")}
          >
            Retour au catalogue
          </button>
          {prestation.prestationGroupLabel && (
            <p className="catalogue-detail-group">{prestation.prestationGroupLabel}</p>
          )}
        </div>

        <h2>{prestation.label}</h2>

        {Array.isArray(prestation.photos) && prestation.photos.length > 0 && (
          <div className="catalogue-detail-gallery">
            {prestation.photos.map((photo, index) => (
              <img
                key={`prestation-photo-${index}`}
                src={resolvePhotoUrl(photo)}
                alt={`${prestation.label} ${index + 1}`}
              />
            ))}
          </div>
        )}

        <p className="catalogue-detail-intro">
          {prestation.description || "Un protocole expert adapte a vos besoins de peau."}
        </p>

        <div className="catalogue-detail-chips">
          <span className="catalogue-detail-chip">
            <strong>Duree</strong>
            <em>{formatDuration(prestation.duration)}</em>
          </span>
          <span className="catalogue-detail-chip">
            <strong>Tarif</strong>
            <em>{formatPrice(prestation.price)}</em>
          </span>
        </div>

        <div className="catalogue-detail-content">
          <div className="catalogue-detail-panel">
            <h3>A propos du soin</h3>
            <p>{prestation.about || "Soin personnalise en institut."}</p>
          </div>
          <div className="catalogue-detail-panel">
            <h3>Bon a savoir</h3>
            <p>{prestation.goodToKnow || "Un diagnostic est realise avant le soin."}</p>
          </div>
        </div>

        <div className="catalogue-detail-actions">
          {!prestation.isComplementOnly && (
            <button
              type="button"
              className="catalogue-detail-btn catalogue-detail-rdv"
              onClick={() => navigate(`/public/rendez-vous/${id}`)}
            >
              Prendre rendez-vous
            </button>
          )}
        </div>

        {!!prestation.isComplementOnly && (
          <div className="catalogue-detail-related">
            <h3>Ce complement peut être ajoute à ces prestations :</h3>
            {Array.isArray(prestation.availableForPrestations) && prestation.availableForPrestations.length > 0 ? (
              <div className="catalogue-detail-related-list">
                {prestation.availableForPrestations.map((item) => (
                  <button
                    key={`related-${item.id}`}
                    type="button"
                    className="catalogue-detail-related-item"
                    onClick={() => navigate(`/public/catalogue/${item.id}`)}
                  >
                    <span>{item.label}</span>
                    <em>{formatDuration(item.duration)} · {formatPrice(item.price)}</em>
                  </button>
                ))}
              </div>
            ) : (
              <p className="catalogue-detail-related-empty">
                Aucun soin principal associe pour le moment.
              </p>
            )}
          </div>
        )}

        {!prestation.isComplementOnly && Array.isArray(prestation.complements) && prestation.complements.length > 0 && (
          <div className="catalogue-detail-related">
            <h3>Complements disponibles pour ce soin :</h3>
            <div className="catalogue-detail-related-list">
              {prestation.complements.map((item) => (
                <button
                  key={`complement-${item.id}`}
                  type="button"
                  className="catalogue-detail-related-item"
                  onClick={() => navigate(`/public/catalogue/${item.id}`)}
                >
                  <span>{item.label}</span>
                  <em>+{formatDuration(item.duration)} · +{formatPrice(item.price)}</em>
                </button>
              ))}
            </div>
          </div>
        )}
      </article>
    </section>
  );
}

export default CatalogueDetail;
